import React, { useEffect, useRef, useState } from "react";
import { api, formatMoney, uploadFile, fileUrl } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, Sandwich as SandwichIcon, ImageIcon, Upload, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY = {
  nombre: "", precio: 0, categoria: "Otros", descripcion: "",
  imagen_path: "", imagen_url: "", aliases: "", activo: true,
};

export default function Productos() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { user } = useAuth();
  const canEdit = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/productos", { params: { incluir_inactivos: true } }),
        api.get("/categorias"),
      ]);
      setRows(r.data);
      setCats(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      nombre: p.nombre, precio: p.precio, categoria: p.categoria || "Otros",
      descripcion: p.descripcion || "", imagen_path: p.imagen_path || "",
      imagen_url: p.imagen_url || "",
      aliases: (p.aliases || []).join(", "), activo: !!p.activo,
    });
    setDialogOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file);
      setForm((f) => ({ ...f, imagen_path: res.path, imagen_url: "" }));
      toast.success("Imagen subida.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir la imagen.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = () => {
    setForm((f) => ({ ...f, imagen_path: "", imagen_url: "" }));
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio."); return; }
    const payload = {
      nombre: form.nombre.trim(),
      precio: Number(form.precio) || 0,
      categoria: form.categoria || "Otros",
      descripcion: form.descripcion || "",
      imagen_path: form.imagen_path || "",
      imagen_url: form.imagen_url || "",
      aliases: (form.aliases || "").split(",").map((s) => s.trim()).filter(Boolean),
      activo: !!form.activo,
    };
    try {
      if (editing) {
        await api.put(`/productos/${editing.id}`, payload);
        toast.success("Producto actualizado.");
      } else {
        await api.post("/productos", payload);
        toast.success("Producto creado.");
      }
      setDialogOpen(false);
      load();
    } catch { toast.error("Error al guardar."); }
  };

  const toggleActivo = async (p) => {
    try {
      await api.put(`/productos/${p.id}`, { activo: !p.activo });
      load();
    } catch { toast.error("Error al cambiar estado."); }
  };

  const remove = async (p) => {
    try { await api.delete(`/productos/${p.id}`); toast.success("Producto eliminado."); load(); }
    catch { toast.error("Error al eliminar."); }
  };

  const previewSrc = form.imagen_path ? fileUrl(form.imagen_path) : (form.imagen_url || "");

  return (
    <div>
      <PageHeader eyebrow="Catálogo" title="Productos" subtitle="Lo que ven los clientes en la tienda. Foto + precio + categoría.">
        {canEdit && <Button className="bg-[#C85A32] hover:bg-[#A64A29] text-white gap-2" onClick={openNew} data-testid="prod-nuevo">
          <Plus className="h-4 w-4" /> Nuevo producto
        </Button>}
      </PageHeader>

      <Card className="border-[#E5E0D8] rounded-2xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F9F6F0] border-b border-[#E5E0D8]">
              <tr className="text-[11px] uppercase tracking-[0.14em] text-[#8C8C8C]">
                <th className="px-5 py-3"></th>
                <th className="px-5 py-3">Producto</th>
                <th className="px-5 py-3">Categoría</th>
                <th className="px-5 py-3 text-right">Precio</th>
                <th className="px-5 py-3">Activo</th>
                {canEdit && <th className="px-5 py-3 text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={canEdit ? 6 : 5} className="text-center text-[#8C8C8C] py-12">Cargando...</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={canEdit ? 6 : 5} className="text-center py-16">
                  <SandwichIcon className="h-10 w-10 text-[#C85A32] mx-auto mb-3 opacity-50" />
                  <div className="font-display font-semibold text-lg">Sin productos</div>
                </td></tr>
              )}
              {!loading && rows.map((p, i) => (
                <tr key={p.id} className="border-b border-[#F0EBE0] hover:bg-[#FBF8F2] fade-row" style={{ animationDelay: `${i * 20}ms` }} data-testid={`prod-row-${p.id}`}>
                  <td className="px-5 py-3 w-[60px]">
                    <div className="h-10 w-10 rounded-md bg-[#F4EAE6] flex items-center justify-center overflow-hidden">
                      {p.imagen_path ? (
                        <img src={fileUrl(p.imagen_path)} alt="" className="h-full w-full object-cover" />
                      ) : p.imagen_url ? (
                        <img src={p.imagen_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <ImageIcon className="h-4 w-4 text-[#C85A32]/60" />
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="font-medium">{p.nombre}</div>
                    {p.descripcion && <div className="text-[11px] text-[#8C8C8C] truncate max-w-[260px]">{p.descripcion}</div>}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <span className="px-2 py-0.5 rounded-full bg-[#F4EAE6] text-[#A64A29] text-[11px] font-medium">{p.categoria || "Otros"}</span>
                  </td>
                  <td className="px-5 py-3 text-right font-mono font-semibold text-[#C85A32]">{formatMoney(p.precio)}</td>
                  <td className="px-5 py-3">
                    {canEdit ? <Switch checked={!!p.activo} onCheckedChange={() => toggleActivo(p)} data-testid={`prod-activo-${p.id}`} /> : (p.activo ? "Sí" : "No")}
                  </td>
                  {canEdit && <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" className="gap-1.5 text-[#595959] hover:text-[#C85A32] hover:bg-[#F4EAE6]" onClick={() => openEdit(p)} data-testid={`prod-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-[#8E2E2E] hover:bg-[#FBEBEB]" data-testid={`prod-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminar producto</AlertDialogTitle>
                            <AlertDialogDescription>¿Eliminar "{p.nombre}"? Considerá desactivarlo en lugar de borrarlo.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Volver</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(p)} className="bg-[#B23A3A] hover:bg-[#8E2E2E]">Eliminar</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={canEdit && dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar producto" : "Nuevo producto"}</DialogTitle>
            <DialogDescription>
              {editing ? "Modificá los datos del producto. Solo el admin puede cambiar precios." : "Completá los datos del nuevo producto."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label>Imagen</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <div className="h-20 w-20 rounded-lg bg-[#F4EAE6] flex items-center justify-center overflow-hidden shrink-0 border border-[#E5E0D8]">
                  {previewSrc ? (
                    <img src={previewSrc} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-[#C85A32]/60" />
                  )}
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleUpload} className="hidden" data-testid="prod-form-file" />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="border-[#E5E0D8] gap-2" data-testid="prod-form-upload">
                      <Upload className="h-4 w-4" />
                      {uploading ? "Subiendo..." : "Subir imagen"}
                    </Button>
                    {previewSrc && (
                      <Button type="button" variant="ghost" onClick={removeImage} className="text-[#8E2E2E] hover:bg-[#FBEBEB] gap-1" data-testid="prod-form-remove-img">
                        <X className="h-4 w-4" /> Quitar
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-[#8C8C8C]">PNG, JPG, WEBP o GIF. Máx 5 MB.</p>
                </div>
              </div>
            </div>
            <div className="col-span-2">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="prod-form-nombre" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                Precio
                {!canEdit && (
                  <span className="text-[10px] uppercase tracking-wider text-[#A6751F] font-semibold bg-[#FDF3E1] px-1.5 py-0.5 rounded">solo admin</span>
                )}
              </Label>
              <Input
                type="number"
                value={form.precio}
                onChange={(e) => setForm({ ...form, precio: e.target.value })}
                data-testid="prod-form-precio"
                disabled={!canEdit}
                title={canEdit ? "" : "Solo el admin puede modificar precios"}
              />
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                <SelectTrigger data-testid="prod-form-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Descripción (opcional)</Label>
              <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={2} placeholder="Pan casero, jamón cocido, queso..." data-testid="prod-form-desc" />
            </div>
            <div className="col-span-2">
              <Label>Aliases (separados por coma)</Label>
              <Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} placeholder="ej: mila pollo grande, milanesa pollo g" data-testid="prod-form-aliases" />
              <p className="text-[11px] text-[#8C8C8C] mt-1">Sólo se usan en Carga rápida (parser por texto).</p>
            </div>
            <div className="col-span-2 flex items-center justify-between border-t border-[#E5E0D8] pt-3">
              <div>
                <Label className="mb-0">Visible en catálogo</Label>
                <p className="text-[11px] text-[#8C8C8C]">Si lo desactivás, los clientes no lo verán.</p>
              </div>
              <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} data-testid="prod-form-activo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#C85A32] hover:bg-[#A64A29] text-white" onClick={save} data-testid="prod-form-save">{editing ? "Actualizar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
